import React from 'react';
import { styled } from '@linaria/react';

import { Key } from './Synthesizer.constants';

interface Props {
  musicalKey: Key;
  isPressed: boolean;
  isNextKeyPressed: boolean;
  isPreviousKeyPressed: boolean;
  handleClickKey: (key: string) => void;
  handleReleaseKey: (key: string) => void;
  isGlissando: boolean;
  setIsGlissando: React.Dispatch<React.SetStateAction<boolean>>;
  layers: number;
}

const KEYS_AFTER_BLACK_KEY = ['D', 'E', 'G', 'A', 'B'];

function KeyWhite({
  musicalKey,
  isPressed,
  isPreviousKeyPressed,
  handleClickKey,
  handleReleaseKey,
  setIsGlissando,
  layers,
}: Props) {
  const followsBlackKey = KEYS_AFTER_BLACK_KEY.includes(musicalKey);

  const handleMouseDown = React.useCallback(() => {
    setIsGlissando(true);
    handleClickKey(musicalKey);
  }, [handleClickKey, musicalKey, setIsGlissando]);

  const handleMouseUp = React.useCallback(() => {
    setIsGlissando(false);
    handleReleaseKey(musicalKey);
  }, [handleReleaseKey, musicalKey, setIsGlissando]);

  const handleMouseLeave = React.useCallback(() => {
    if (isPressed) {
      handleReleaseKey(musicalKey);
    }
  }, [handleReleaseKey, musicalKey, isPressed]);

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent) => {
      // Primary button (usually left) is pressed
      if (e.buttons === 1) {
        handleClickKey(musicalKey);
      }
    },
    [handleClickKey, musicalKey]
  );

  return (
    <Wrapper
      data-is-pressed={isPressed}
      data-is-previous-key-pressed={isPreviousKeyPressed}
      data-hide-box-shadow={layers < 4}
      data-hide-key-edge={layers < 2}
      data-hide-top-shadow={layers < 1}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <KeyEdge />
      <KeyFace>{followsBlackKey && <BlackKeyShadow />}</KeyFace>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --edge-height: 4px;
  position: relative;
  flex: 1;
  height: 100%;
  background: black;
  padding: 1px;

  &[data-hide-key-edge='true'] {
    --edge-height: 0px;
  }
`;

const KeyFace = styled.div`
  position: absolute;
  top: 0;
  left: 0.5px;
  right: 0.5px;
  bottom: var(--edge-height);
  background: linear-gradient(
    to bottom,
    hsl(0deg 0% 100%),
    hsl(0deg 0% 80%)
  );
  box-shadow:
    inset 0px 0.5px 0.5px 1px hsl(0deg 0% 0% / 0.4),
    inset 0px 0px 0.5px 1.5px hsl(0deg 0% 100% / 0.5),
    inset 0px 0px 0.5px 2.5px hsl(0deg 0% 100% / 0.5);
  border-radius: 0 0 2px 2px;
  overflow: hidden;
  overflow: clip;

  ${Wrapper}[data-hide-box-shadow='true'] & {
    box-shadow: none;
  }

  ${Wrapper}[data-hide-key-edge='true'] & {
    border-radius: 0;
  }

  /*
    Add the shadow near the top of the key, to show that the keybed is lower than the top panel.
    This can't just be a layered gradient because it needs to sit above the inset box-shadows.
  */
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    height: 16px;
    filter: blur(3px);
    background: hsl(0deg 0% 0% / 0.5);
  }

  ${Wrapper}[data-hide-top-shadow='true'] &::before {
    display: none;
  }

  ${Wrapper}[data-is-pressed='true'] & {
    bottom: 2px;
    background: linear-gradient(
      to bottom,
      hsl(0deg 0% 100%),
      hsl(0deg 0% 65%)
    );
    box-shadow:
      inset 0px 0.5px 0.5px 1px hsl(0deg 0% 0% / 0.6),
      inset 0px 0px 0.5px 1.5px hsl(0deg 0% 100% / 0.25),
      inset 0px 0px 0.5px 2.5px hsl(0deg 0% 100% / 0.25);

    /* Add the diagonal shadow from the key to the left */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -50%;
      bottom: -20%;
      width: 100%;
      background: hsl(0deg 0% 0% / 0.5);
      filter: blur(5px);
      transform-origin: bottom left;
      transform: rotate(-10deg);
    }
  }

  ${Wrapper}[data-is-previous-key-pressed='true'] & {
    &::after {
      display: none;
    }
  }
`;

const KeyEdge = styled.div`
  position: absolute;
  left: 1px;
  right: 1px;
  bottom: 0;
  height: calc(var(--edge-height) + 2px);
  background: linear-gradient(
    to bottom,
    hsl(0deg 0% 20%),
    hsl(0deg 0% 60%)
  );
  border-radius: 0 0 2px 2px;

  ${Wrapper}[data-is-pressed='true'] & {
    height: 4px;
    background: linear-gradient(
        to right,
        hsl(0deg 0% 0% / 0.6),
        hsl(0deg 0% 0% / 0)
      ),
      hsl(0deg 0% 40%);
  }

  ${Wrapper}[data-hide-key-edge='true'] & {
    display: none;
  }
`;

const BlackKeyShadow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60%;
  width: 50%;
  background: hsl(0deg 0% 0% / 0.4);
  filter: blur(3px);
  transform: rotate(4deg);
  transform-origin: top right;

  ${Wrapper}[data-is-pressed='true'] & {
    background: hsl(0deg 0% 0% / 0.6);
    width: 55%;
  }
`;

export default React.memo(KeyWhite);
